import { createSelector } from 'reselect'
import { namespace } from '.'
import { getMarketClosed } from '../market/selectors'
import { Order, OrderType } from '../order/types'
import { Security } from '../securities/reducer'
import { getTypedArray } from '../securities/helpers'
import {
  getSecurityOrderDataById,
  getSecurityStaticDataById
} from '../securities/selectors'
import { State } from './reducer'

export const getState = (state: { [namespace]: State }) => state[namespace]

export const getPopoutSecurityIds = createSelector(
  [getState],
  (state) => state.popoutSecurityIds
)

export const getAllDOMOrders = createSelector([getState], (state) =>
  Object.values(state.depthBySecurity).flat()
)

export const getDomOrderById = createSelector(
  [getAllDOMOrders],
  (orders) => (orderId: string) => orders.find((o) => o.id === orderId)
)

export const getDOMLimitExceeded = createSelector(
  [getState],
  (state) => state.domLimitExceeded
)

export const getMaxDOMs = createSelector([getState], (state) => state.maxDOMs)

export const getDepthOfMarketOrder = createSelector(
  [getAllDOMOrders, getSecurityOrderDataById],
  (orders, getSecurity) =>
    (orderId: Order['id'], securityId: Security['id']) => {
      const security = getSecurity(securityId)
      if (security?.bestBid?.id === orderId) {
        return security.bestBid
      }
      if (security?.bestOffer?.id === orderId) {
        return security.bestOffer
      }
      return orders.find((order) => order.id === orderId)
    }
)

export const getMarketForSecurity = createSelector(
  [getState, getMarketClosed],
  (state, marketIsClosed) => (securityId: Security['id'], type: OrderType) =>
    (state.depthBySecurity[securityId] ?? [])
      .filter((order) => order.type === type)
      .map((order) =>
        marketIsClosed ? { ...order, canAggress: false } : order
      )
)

export const getDepthSecurities = createSelector(
  [getPopoutSecurityIds, getSecurityStaticDataById],
  (popoutIds, getSecurityData) => {
    return popoutIds
      .map((id) => getSecurityData(id))
      .filter((security) => !!security?.id)
  }
)

const emptyOrders = getTypedArray<Order>()
export const getDepthForSecurity = createSelector(
  [getState],
  (state) => (securityId: number) =>
    state.depthBySecurity[securityId] ?? emptyOrders
)

export const getDepthForSecurityByType = createSelector(
  [getState, getMarketClosed],
  (state, marketIsClosed) => (securityId: number, type: OrderType) =>
    (state.depthBySecurity[securityId] ?? emptyOrders)
      .filter((order) => order.type === type)
      .map((order) =>
        marketIsClosed ? { ...order, canAggress: false } : order
      )
)

export const getApiCallState = createSelector(
  [getState],
  (state) => (securityId: number, callId: string) =>
    state.apiCalls[`${securityId}-${callId}`]
)
